// The "Sound Vault" — curated pre-built sounds. Click a card and a
// fully-wired voice statement gets appended to the song and scrolled
// into view. Search narrows by label / sample / tags; category chips
// filter by family.
//
// Always appends at the top level — the section/stack target picker
// went away with the visual block editor. The agent is the surface
// for "insert this voice into that section."

import { useEffect, useMemo, useRef, useState } from 'react';
import { useEditContext } from '../editContext';

export type PreviewOps = {
  /** The currently auditioning sound-library entry id, or null. */
  activeId: string | null;
  /** False while the audio engine is still booting. */
  canPlay: boolean;
  /** Start an audition. The optional durationMs overrides the default
   *  preview length. */
  start: (id: string, code: string, label: string, durationMs?: number) => void;
  stop: () => void;
};
import {
  CATEGORY_META,
  CATEGORY_ORDER,
  codeStripFor,
  makeSoundStatement,
  previewCodeFor,
  SOUND_CATALOG,
  type SoundCategory,
  type SoundEntry,
} from './catalog';
import { SoundGlyph } from './SoundGlyph';

type Filter = 'all' | SoundCategory;

type Props = {
  preview: PreviewOps;
};

export function SoundLibrary({ preview }: Props) {
  const ops = useEditContext();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [flashId, setFlashId] = useState<string | null>(null);
  const flashTimer = useRef<number | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SOUND_CATALOG.filter((entry) => {
      if (filter !== 'all' && entry.category !== filter) return false;
      if (!q) return true;
      if (entry.label.toLowerCase().includes(q)) return true;
      if (entry.type !== 'section' && entry.sample.toLowerCase().includes(q))
        return true;
      if (entry.tags.some((t) => t.toLowerCase().includes(q))) return true;
      if (entry.type === 'section' && entry.varName.toLowerCase().includes(q))
        return true;
      return false;
    });
  }, [query, filter]);

  const counts = useMemo(() => countByCategory(SOUND_CATALOG), []);

  const insert = (sound: SoundEntry) => {
    let insertedId: string | null = null;
    ops.update((draft) => {
      const taken = new Set<string>();
      for (const s of draft.statements) {
        if (s.kind === 'const') taken.add(s.name);
      }
      const stmt = makeSoundStatement(sound, taken);
      draft.statements.push(stmt);
      insertedId = stmt.id;
    });

    if (insertedId) ops.revealBlock(insertedId);

    setFlashId(sound.id);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlashId(null), 550);
  };

  return (
    <div
      className="relative w-full h-full bg-zinc-950 flex flex-col min-h-0"
      aria-label="Sound library"
    >
      <LibrarySearch
        ref={searchRef}
        value={query}
        onChange={setQuery}
        visible={filtered.length}
        total={SOUND_CATALOG.length}
      />
      <CategoryChips filter={filter} setFilter={setFilter} counts={counts} />

      <div
        aria-label="Pre-built sounds"
        className="flex-1 min-h-0 overflow-y-auto px-3 pb-6 pt-1 space-y-1.5 scrollbar-thin"
      >
        {filtered.length === 0 ? (
          <EmptyState query={query} />
        ) : (
          filtered.map((entry) => (
            <SoundCard
              key={entry.id}
              entry={entry}
              flashing={flashId === entry.id}
              previewing={preview.activeId === entry.id}
              canPreview={preview.canPlay}
              onInsert={() => insert(entry)}
              onPreviewToggle={() => {
                if (preview.activeId === entry.id) {
                  preview.stop();
                } else {
                  preview.start(entry.id, previewCodeFor(entry), entry.label);
                }
              }}
            />
          ))
        )}
      </div>

    </div>
  );
}

// ─── Search ─────────────────────────────────────────────────────────

type SearchProps = {
  value: string;
  onChange: (v: string) => void;
  visible: number;
  total: number;
  ref?: React.Ref<HTMLInputElement>;
};

function LibrarySearch({ value, onChange, visible, total, ref }: SearchProps) {
  return (
    <div className="px-4 pt-3 pb-2">
      <label className="relative block">
        <span className="sr-only">Search sounds</span>
        <svg
          viewBox="0 0 16 16"
          className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600"
          stroke="currentColor"
          strokeWidth={1.5}
          fill="none"
        >
          <circle cx={7} cy={7} r={4.5} />
          <path d="M10.5 10.5 L14 14" strokeLinecap="round" />
        </svg>
        <input
          ref={ref}
          type="text"
          inputMode="search"
          placeholder="search kick, saw, tabla…"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-zinc-900/70 border border-zinc-800 focus:border-yellow-300/50 focus:bg-zinc-900 focus:outline-none rounded text-[11px] text-zinc-200 placeholder:text-zinc-600 placeholder:tracking-wide pl-8 pr-12 py-1.5 transition-colors font-body"
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-zinc-600 tabular-nums tracking-wider">
          {visible}
          <span className="text-zinc-700">/{total}</span>
        </span>
      </label>
    </div>
  );
}

// ─── Category chips ─────────────────────────────────────────────────

function CategoryChips({
  filter,
  setFilter,
  counts,
}: {
  filter: Filter;
  setFilter: (f: Filter) => void;
  counts: Record<SoundCategory, number>;
}) {
  return (
    <div
      role="group"
      aria-label="Filter by category"
      className="px-3 pb-2.5 flex flex-wrap gap-1"
    >
      <Chip
        active={filter === 'all'}
        onClick={() => setFilter('all')}
        label="All"
        accent="bg-yellow-300"
        count={Object.values(counts).reduce((a, b) => a + b, 0)}
      />
      {CATEGORY_ORDER.map((cat) => (
        <Chip
          key={cat}
          active={filter === cat}
          onClick={() => setFilter(cat)}
          label={CATEGORY_META[cat].label}
          accent={CATEGORY_META[cat].accent}
          count={counts[cat] ?? 0}
        />
      ))}
    </div>
  );
}

function Chip({
  active,
  onClick,
  label,
  accent,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  accent: string;
  count: number;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={
        'group inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border text-[9px] tracking-[0.2em] uppercase font-display transition-colors ' +
        (active
          ? 'bg-zinc-100 text-zinc-950 border-zinc-100'
          : 'bg-zinc-900/50 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200')
      }
    >
      <span
        className={
          'h-1.5 w-1.5 rounded-full ' +
          accent +
          (active ? '' : ' opacity-80')
        }
        aria-hidden="true"
      />
      <span>{label}</span>
      <span
        className={
          'tabular-nums text-[9px] tracking-normal ' +
          (active ? 'text-zinc-500' : 'text-zinc-600')
        }
      >
        {count}
      </span>
    </button>
  );
}

// ─── Card ───────────────────────────────────────────────────────────

function SoundCard({
  entry,
  flashing,
  previewing,
  canPreview,
  onInsert,
  onPreviewToggle,
}: {
  entry: SoundEntry;
  flashing: boolean;
  previewing: boolean;
  canPreview: boolean;
  onInsert: () => void;
  onPreviewToggle: () => void;
}) {
  const meta = CATEGORY_META[entry.category];
  return (
    <div
      data-flashing={flashing || undefined}
      data-previewing={previewing || undefined}
      className={
        'sound-card group relative w-full overflow-hidden ' +
        'bg-zinc-900/40 hover:bg-zinc-900 ' +
        'border border-zinc-800 hover:border-zinc-700 ' +
        'rounded-[3px] transition-all duration-150 flex items-stretch ' +
        (previewing ? 'border-yellow-300/50 bg-zinc-900 ' : '') +
        meta.glow
      }
    >
      {/* Accent stripe — category color on the left edge. */}
      <span
        className={'absolute left-0 top-0 bottom-0 w-[3px] ' + meta.accent}
        aria-hidden="true"
      />
      {/* Scanline sheen on hover. */}
      <span
        className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-yellow-300/[0.04] to-transparent opacity-0 group-hover:opacity-100 -translate-x-full group-hover:translate-x-full transition-all duration-700"
        aria-hidden="true"
      />

      {/* Preview button — vertical rail on the left, next to accent. */}
      <button
        type="button"
        onClick={onPreviewToggle}
        disabled={!canPreview}
        aria-label={previewing ? `Stop previewing ${entry.label}` : `Preview ${entry.label}`}
        aria-pressed={previewing}
        title={
          canPreview
            ? previewing
              ? 'Stop preview'
              : 'Preview this sound'
            : 'Audio engine still booting…'
        }
        className={
          'preview-btn shrink-0 w-9 self-stretch flex items-center justify-center ' +
          'border-r border-zinc-800/80 transition-colors ' +
          'focus-visible:outline focus-visible:outline-1 focus-visible:outline-yellow-300/60 focus-visible:outline-offset-[-2px] ' +
          (canPreview
            ? 'text-zinc-500 hover:text-yellow-300 hover:bg-zinc-900 cursor-pointer'
            : 'text-zinc-700 cursor-not-allowed') +
          (previewing ? ' text-yellow-300 bg-yellow-300/5' : '')
        }
      >
        {previewing ? <StopIcon /> : <PlayIcon />}
      </button>

      {/* Main card body — click-to-insert. */}
      <button
        type="button"
        onClick={onInsert}
        aria-label={insertAriaLabel(entry)}
        className={
          'flex-1 min-w-0 text-left ' +
          'focus-visible:outline focus-visible:outline-1 focus-visible:outline-yellow-300/60 focus-visible:outline-offset-[-2px] ' +
          'pl-3 pr-3 py-2.5 flex items-center gap-3 ' +
          'hover:bg-zinc-900/60 transition-colors'
        }
      >
        <div className={'w-[60px] h-[22px] shrink-0 ' + meta.dot}>
          <SoundGlyph kind={entry.glyph} className="w-full h-full" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="font-mono text-[12px] text-zinc-100 truncate group-hover:text-yellow-300 transition-colors">
              {entry.type === 'section' ? entry.varName : entry.sample}
            </span>
            <span className="font-display text-[9px] tracking-[0.22em] uppercase text-zinc-500 truncate">
              {entry.label}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5 min-w-0">
            <span className={'text-[8px] tracking-[0.25em] uppercase font-display ' + meta.dot}>
              {entry.type === 'section'
                ? `${meta.label} · ${entry.voices.length} voices · ${entry.duration}`
                : meta.label}
            </span>
            <span className="text-[9px] text-zinc-600 truncate">{entry.blurb}</span>
          </div>
          <div className="mt-1 text-[9px] font-mono text-zinc-600 truncate">
            <span className="text-zinc-700">»</span>{' '}
            <span className="text-zinc-400">{codeStripFor(entry)}</span>
          </div>
        </div>

        {/* Insert affordance — appears on hover. */}
        <span
          aria-hidden="true"
          className="shrink-0 self-stretch flex items-center text-[9px] tracking-[0.22em] uppercase font-display text-zinc-700 group-hover:text-yellow-300 transition-colors"
        >
          drop →
        </span>
      </button>
    </div>
  );
}

function insertAriaLabel(entry: SoundEntry): string {
  if (entry.type === 'section') {
    return `Insert ${entry.label} section (${entry.voices.length} voices) into song`;
  }
  return `Insert ${entry.label} (${entry.sample}) into song`;
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 14 14" className="w-3 h-3" fill="currentColor" aria-hidden="true">
      <path d="M3 1.5 L11.5 7 L3 12.5 Z" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg viewBox="0 0 14 14" className="w-3 h-3" fill="currentColor" aria-hidden="true">
      <rect x={2.5} y={2.5} width={9} height={9} rx={0.5} />
    </svg>
  );
}

// ─── Empty state ────────────────────────────────────────────────────

function EmptyState({ query }: { query: string }) {
  return (
    <div className="py-16 px-4 text-center">
      <div className="font-display text-[10px] tracking-[0.32em] uppercase text-zinc-600 mb-2">
        nothing found
      </div>
      <p className="text-[11px] text-zinc-500 leading-[1.5]">
        {query ? (
          <>
            No sounds match <span className="font-mono text-zinc-300">"{query}"</span>.
          </>
        ) : (
          <>This category is empty.</>
        )}
      </p>
    </div>
  );
}

function countByCategory(list: SoundEntry[]): Record<SoundCategory, number> {
  const out: Record<SoundCategory, number> = {
    structure: 0,
    drums: 0,
    perc: 0,
    bass: 0,
    lead: 0,
    keys: 0,
    pad: 0,
    fx: 0,
  };
  for (const e of list) out[e.category]++;
  return out;
}
